import type { ReactNode } from 'react'
import {
  formatCrmDateMsk,
  formatCrmDateTimeMsk,
  formatCrmTimeMsk,
} from '@pages/Admin/CRM/utils/formatData'
import type { TaskTrackerProjectMember } from '../store/taskTracker.types'
import type { TaskTrackerTaskHistoryEvent } from '../store/taskTracker.types'

export type TaskTrackerTaskHistoryLookups = {
  columnTitlesById: Map<number, string>
  stickerTitlesById: Map<number, string>
  performerNamesById: Map<number, string>
}

const trimValue = (value: unknown) =>
  value === null || value === undefined ? '' : String(value).trim()

const Em = ({ children }: { children: ReactNode }) => (
  <strong>{children}</strong>
)

const TASK_FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  deadline: 'Дедлайн',
  is_completed: 'Статус',
  performer_id: 'Исполнитель',
  performer_ids: 'Исполнители',
}

const TASK_HISTORY_AUTOMATION_ACTION_LABELS: Record<string, string> = {
  move_task: 'Автоматический перенос',
  complete_task: 'Автоматическое выполнение',
  archive_task: 'Автоматическое архивирование',
  remove_performer: 'Автоматическое снятие исполнителей',
}

export const getTaskHistoryAutomationAction = (
  eventData: Record<string, unknown> | null | undefined,
) => {
  const action = trimValue(eventData?.automation_action)
  return action || null
}

export const isTaskHistoryAutomationEvent = (
  event: Pick<TaskTrackerTaskHistoryEvent, 'eventData'>,
) => Boolean(getTaskHistoryAutomationAction(event.eventData))

export const getTaskHistoryAutomationBadgeLabel = (action: string) =>
  TASK_HISTORY_AUTOMATION_ACTION_LABELS[action] ?? 'Автоматизация'

const formatBooleanValue = (value: unknown) => {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return 'Выполнена'
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return 'В работе'
  }
  return trimValue(value) || '—'
}

const formatDeadlineValue = (value: unknown) => {
  const raw = trimValue(value)
  if (!raw) return 'Не указан'
  return formatCrmDateMsk(raw)
}

const resolvePerformerName = (
  performerId: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  const id = Number(performerId)
  if (!Number.isFinite(id) || id <= 0) return 'Не назначен'
  return lookups.performerNamesById.get(id) ?? `ID ${id}`
}

const formatPerformerIdsValue = (
  value: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  if (!Array.isArray(value) || value.length === 0) {
    return 'Не назначены'
  }

  return value
    .map((performerId) => resolvePerformerName(performerId, lookups))
    .join(', ')
}

const formatFieldChangeValue = (
  fieldKey: string,
  value: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  if (fieldKey === 'is_completed') return formatBooleanValue(value)
  if (fieldKey === 'deadline') return formatDeadlineValue(value)
  if (fieldKey === 'performer_id') return resolvePerformerName(value, lookups)
  if (fieldKey === 'performer_ids') {
    return formatPerformerIdsValue(value, lookups)
  }
  return trimValue(value) || '—'
}

const renderChangeLine = (
  label: string,
  fromValue: unknown,
  toValue: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
  fieldKey?: string,
) => {
  const fromRaw = fieldKey
    ? formatFieldChangeValue(fieldKey, fromValue, lookups)
    : trimValue(fromValue) || '—'
  const toRaw = fieldKey
    ? formatFieldChangeValue(fieldKey, toValue, lookups)
    : trimValue(toValue) || '—'

  if (fromRaw !== '—' && toRaw !== '—') {
    return (
      <>
        <Em>{label}</Em>: <Em>{fromRaw}</Em> → <Em>{toRaw}</Em>
      </>
    )
  }

  if (fromRaw === '—' && toRaw !== '—') {
    return (
      <>
        <Em>{label}</Em>: <Em>{toRaw}</Em>
      </>
    )
  }

  if (fromRaw !== '—' && toRaw === '—') {
    return (
      <>
        <Em>{label}</Em>: очищено (было «<Em>{fromRaw}</Em>»)
      </>
    )
  }

  return (
    <>
      <Em>{label}</Em>: изменено
    </>
  )
}

const UNKNOWN_COLUMN_TITLE = 'Неизвестная колонка'

const resolveColumnTitle = (
  columnId: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  const id = Number(columnId)
  // null/0 column_id в архивации = архив; для move без id — см. resolveMoveLocation
  if (!Number.isFinite(id) || id <= 0) return 'Архив'
  return lookups.columnTitlesById.get(id) ?? `Колонка #${id}`
}

type MoveLocationResolved = {
  columnTitle: string
  boardTitle: string
  boardId: number | null
  projectId: number | null
}

/**
 * Локация переноса: новый контракт — nested from/to со snapshot-заголовками;
 * старый — from_column_id / to_column_id (+ lookup по текущим колонкам доски).
 */
const resolveMoveLocation = (
  eventData: Record<string, unknown>,
  side: 'from' | 'to',
  lookups: TaskTrackerTaskHistoryLookups,
): MoveLocationResolved => {
  const nested = eventData[side]
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const location = nested as Record<string, unknown>
    const snapshotTitle = trimValue(location.column_title)
    const boardTitle = trimValue(location.board_title)
    const boardIdNum = Number(location.board_id)
    const projectIdNum = Number(location.project_id)

    let columnTitle = snapshotTitle
    if (!columnTitle && location.column_id != null) {
      columnTitle = resolveColumnTitle(location.column_id, lookups)
    }
    if (!columnTitle) {
      columnTitle = UNKNOWN_COLUMN_TITLE
    }

    return {
      columnTitle,
      boardTitle,
      boardId:
        Number.isFinite(boardIdNum) && boardIdNum > 0 ? boardIdNum : null,
      projectId:
        Number.isFinite(projectIdNum) && projectIdNum > 0
          ? projectIdNum
          : null,
    }
  }

  const legacyId =
    side === 'from' ? eventData.from_column_id : eventData.to_column_id

  return {
    columnTitle: resolveColumnTitle(legacyId, lookups),
    boardTitle: '',
    boardId: null,
    projectId: null,
  }
}

const formatMoveLocationLabel = (
  location: MoveLocationResolved,
  includeBoard: boolean,
) => {
  if (includeBoard && location.boardTitle) {
    return `${location.boardTitle} · ${location.columnTitle}`
  }
  return location.columnTitle
}

const resolveMoveEndpointLabels = (
  eventData: Record<string, unknown>,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  const from = resolveMoveLocation(eventData, 'from', lookups)
  const to = resolveMoveLocation(eventData, 'to', lookups)

  const includeBoard =
    (from.boardId != null &&
      to.boardId != null &&
      from.boardId !== to.boardId) ||
    (from.projectId != null &&
      to.projectId != null &&
      from.projectId !== to.projectId) ||
    (Boolean(from.boardTitle) &&
      Boolean(to.boardTitle) &&
      from.boardTitle !== to.boardTitle)

  return {
    from: formatMoveLocationLabel(from, includeBoard),
    to: formatMoveLocationLabel(to, includeBoard),
  }
}

const resolveStickerTitle = (
  fieldSettingId: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  const id = Number(fieldSettingId)
  if (!Number.isFinite(id) || id <= 0) return 'Стикер'
  return lookups.stickerTitlesById.get(id) ?? `Стикер #${id}`
}

/** value: string | JSON-string multi-select | null (очистка). */
const formatStickerValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null

  const raw = trimValue(value)
  if (!raw) return null

  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        const joined = parsed
          .map((item) => trimValue(item))
          .filter(Boolean)
          .join(', ')
        return joined || null
      }
    } catch {
      // оставляем как есть
    }
  }

  return raw
}

const renderStickerChangeLine = (
  fieldSettingId: unknown,
  value: unknown,
  lookups: TaskTrackerTaskHistoryLookups,
) => {
  const stickerTitle = resolveStickerTitle(fieldSettingId, lookups)
  const formatted = formatStickerValue(value)

  if (formatted == null) {
    return (
      <>
        <Em>{stickerTitle}</Em>: очищен
      </>
    )
  }

  return (
    <>
      <Em>{stickerTitle}</Em>: <Em>{formatted}</Em>
    </>
  )
}

const collectTaskUpdatedLines = (
  eventData: Record<string, unknown>,
  lookups: TaskTrackerTaskHistoryLookups,
): ReactNode[] => {
  const lines: ReactNode[] = []
  const changes = eventData.changes
  let stickerHandled = false

  if (changes && typeof changes === 'object' && !Array.isArray(changes)) {
    const changesObject = changes as Record<string, unknown>

    if (
      changesObject.field_values &&
      typeof changesObject.field_values === 'object' &&
      !Array.isArray(changesObject.field_values)
    ) {
      const stickerChange = changesObject.field_values as Record<
        string,
        unknown
      >
      lines.push(
        renderStickerChangeLine(
          stickerChange.field_setting_id,
          stickerChange.value,
          lookups,
        ),
      )
      stickerHandled = true
    }

    Object.entries(changesObject).forEach(([fieldKey, rawChange]) => {
      // field_values — отдельный блок выше; completed_at — скаляр рядом с is_completed
      if (fieldKey === 'field_values' || fieldKey === 'completed_at') return
      if (
        !rawChange ||
        typeof rawChange !== 'object' ||
        Array.isArray(rawChange)
      ) {
        return
      }

      const change = rawChange as Record<string, unknown>
      const label = TASK_FIELD_LABELS[fieldKey] ?? fieldKey

      lines.push(
        renderChangeLine(label, change.from, change.to, lookups, fieldKey),
      )
    })
  }

  if (eventData.field_setting_id != null && !stickerHandled) {
    lines.push(
      renderStickerChangeLine(
        eventData.field_setting_id,
        eventData.value,
        lookups,
      ),
    )
  }

  return lines
}

const collectDescriptionDiffLines = (
  eventData: Record<string, unknown>,
): ReactNode[] => {
  const changes = eventData.changes
  if (!Array.isArray(changes)) return []

  return changes
    .map((rawChange, index) => {
      if (!rawChange || typeof rawChange !== 'object' || Array.isArray(rawChange)) {
        return null
      }

      const change = rawChange as Record<string, unknown>
      const op = trimValue(change.op)
      const text = trimValue(change.text) || '—'
      const lineNumber = Number(change.line)

      if (op === 'add') {
        return (
          <span key={`add-${index}`}>
            Добавлена строка {Number.isFinite(lineNumber) ? lineNumber : '—'}:{' '}
            <Em>{text}</Em>
          </span>
        )
      }

      if (op === 'remove') {
        return (
          <span key={`remove-${index}`}>
            Удалена строка {Number.isFinite(lineNumber) ? lineNumber : '—'}:{' '}
            <Em>{text}</Em>
          </span>
        )
      }

      return null
    })
    .filter(Boolean) as ReactNode[]
}

export const buildTaskHistoryPerformerNamesFromAccessibleUsers = (
  users: Array<{ userId: number; name: string }>,
) => {
  const map = new Map<number, string>()

  users.forEach((user) => {
    if (!Number.isFinite(user.userId) || user.userId <= 0) return

    map.set(user.userId, user.name.trim() || `ID ${user.userId}`)
  })

  return map
}

export const buildTaskHistoryPerformerNames = (
  members: TaskTrackerProjectMember[],
) => {
  const map = new Map<number, string>()

  members.forEach((member) => {
    if (!Number.isFinite(member.userId) || member.userId <= 0) return

    const name = member.user?.name?.trim()
    map.set(member.userId, name || `ID ${member.userId}`)
  })

  return map
}

const resolveDescriptionUpdatedEventLabel = (
  event: Pick<TaskTrackerTaskHistoryEvent, 'eventData' | 'eventLabel'>,
) => {
  const subtype = trimValue(event.eventData?.type)

  // Контракт BE: description_added → «Добавлено описание»; иначе — event_label с бэка
  if (subtype === 'description_added') {
    return event.eventLabel || 'Добавлено описание'
  }

  return event.eventLabel || 'Описание задачи обновлено'
}

export const resolveTaskHistoryEventLabel = (
  event: Pick<TaskTrackerTaskHistoryEvent, 'eventType' | 'eventData' | 'eventLabel'>,
) => {
  if (event.eventType === 'task_description_updated') {
    return resolveDescriptionUpdatedEventLabel(event)
  }

  // неизвестный тип: BE кладёт event_type в event_label; иначе показываем raw type
  return event.eventLabel || event.eventType || 'Событие'
}

export const buildTaskHistoryEventContent = (
  event: TaskTrackerTaskHistoryEvent,
  lookups: TaskTrackerTaskHistoryLookups,
): ReactNode => {
  const eventData = event.eventData ?? {}
  const automationAction = getTaskHistoryAutomationAction(eventData)

  switch (event.eventType) {
    case 'task_created': {
      const title = trimValue(eventData.title) || 'Без названия'

      if (trimValue(eventData.action) === 'copied') {
        return (
          <>
            Создана копия задачи «<Em>{title}</Em>»
          </>
        )
      }

      if (trimValue(eventData.source) === 'recurring_task') {
        return (
          <>
            Создана по расписанию «<Em>{title}</Em>»
          </>
        )
      }

      return (
        <>
          Создана задача «<Em>{title}</Em>»
        </>
      )
    }

    case 'task_updated': {
      if (automationAction === 'complete_task') {
        return <>Автоматически отмечена выполненной</>
      }

      if (automationAction === 'remove_performer') {
        const lines = collectTaskUpdatedLines(eventData, lookups)

        if (lines.length > 0) {
          return (
            <>
              <span>Автоматически сняты исполнители</span>
              <ul>
                {lines.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </>
          )
        }

        return <>Автоматически сняты исполнители</>
      }

      const lines = collectTaskUpdatedLines(eventData, lookups)

      return lines.length > 0 ? (
        <ul>
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : (
        <span>Изменения без детализации</span>
      )
    }

    case 'task_description_updated': {
      const subtype = trimValue(eventData.type)

      if (subtype === 'description_added') {
        return (
          <span>{trimValue(eventData.text) || 'Добавлено описание'}</span>
        )
      }

      if (subtype === 'description_deleted') {
        return <span>{trimValue(eventData.text) || 'Описание удалено'}</span>
      }

      const lines = collectDescriptionDiffLines(eventData)

      return lines.length > 0 ? (
        <ul>
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      ) : (
        <span>{trimValue(eventData.text) || 'Описание изменено'}</span>
      )
    }

    case 'task_moved': {
      const { from: fromColumn, to: toColumn } = resolveMoveEndpointLabels(
        eventData,
        lookups,
      )

      if (automationAction === 'move_task') {
        return (
          <>
            Автоматически перенесена из «<Em>{fromColumn}</Em>» в «
            <Em>{toColumn}</Em>»
          </>
        )
      }

      return (
        <>
          Перемещена из «<Em>{fromColumn}</Em>» в «<Em>{toColumn}</Em>»
        </>
      )
    }

    case 'task_deleted': {
      const title = trimValue(eventData.title) || 'Без названия'
      return (
        <>
          Удалена задача «<Em>{title}</Em>»
        </>
      )
    }

    case 'task_completed': {
      if (automationAction === 'complete_task') {
        return <>Автоматически отмечена выполненной</>
      }

      return <>Задача отмечена выполненной</>
    }

    case 'task_performer_changed': {
      const nextPerformers = formatPerformerIdsValue(
        eventData.performer_ids,
        lookups,
      )
      const previousPerformers = formatPerformerIdsValue(
        eventData.previous_performer_ids,
        lookups,
      )

      if (automationAction === 'remove_performer') {
        return (
          <>
            Автоматически сняты исполнители (было: «
            <Em>{previousPerformers}</Em>»)
          </>
        )
      }

      if (nextPerformers === 'Не назначены') {
        return (
          <>
            Сняты исполнители (было: «<Em>{previousPerformers}</Em>»)
          </>
        )
      }

      if (previousPerformers === 'Не назначены') {
        return (
          <>
            Назначены исполнители: «<Em>{nextPerformers}</Em>»
          </>
        )
      }

      return (
        <>
          Исполнители изменены: «<Em>{previousPerformers}</Em>» →{' '}
          <Em>{nextPerformers}</Em>
        </>
      )
    }

    case 'task_archived': {
      // Спека: from_column_id / to_column_id; nested from тоже терпим
      const fromColumn = resolveMoveLocation(
        eventData,
        'from',
        lookups,
      ).columnTitle

      if (automationAction === 'archive_task') {
        return (
          <>
            Автоматически архивирована из «<Em>{fromColumn}</Em>»
          </>
        )
      }

      return (
        <>
          Задача архивирована из «<Em>{fromColumn}</Em>»
        </>
      )
    }

    default:
      return (
        <span>{event.eventLabel || event.eventType || 'Событие'}</span>
      )
  }
}

export const formatTaskHistoryDate = (createdAt: string) =>
  formatCrmDateMsk(createdAt)

export const formatTaskHistoryTime = (createdAt: string) =>
  formatCrmTimeMsk(createdAt)

export const formatTaskHistoryDateTime = (createdAt: string) =>
  formatCrmDateTimeMsk(createdAt)
